import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { filter, pairwise } from 'rxjs';

import { WashService } from './wash/wash.service';
import { RestService } from './rest/rest.service';
import { RefillService } from './refill/refill.service';
import { MagnetService } from './magnet/magnet.service';
import { RobotService } from './robot/robot.service';
import { PersonalTripService } from './personal-trip/personal-trip.service';

export interface ApprovalNotification {
  title: string;
  type: 'approved' | 'rejected';
}

@Injectable({ providedIn: 'root' })
export class ApprovalNotificationService {
  private readonly router = inject(Router);
  private readonly washService = inject(WashService);
  private readonly restService = inject(RestService);
  private readonly refillService = inject(RefillService);
  private readonly magnetService = inject(MagnetService);
  private readonly robotService = inject(RobotService);
  private readonly personalTripService = inject(PersonalTripService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _notification = signal<ApprovalNotification | null>(null);
  readonly notification = this._notification.asReadonly();

  constructor() {
    toObservable(this.washService.step).pipe(
      pairwise(),
      filter(([prev, curr]) => prev !== 'approved' && curr === 'approved' && prev !== 'cancel-pending'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/wash')) {
        this._notification.set({ title: 'Мийка схвалена оператором', type: 'approved' });
      }
    });

    this.washService.rejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/wash')) {
        this._notification.set({ title: 'Запит на мийку відхилено оператором', type: 'rejected' });
      }
    });

    this.washService.cancelApprovedConfirmed$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/wash')) {
        this._notification.set({ title: 'Запит на скасування мийки схвалено', type: 'approved' });
      }
    });

    this.washService.cancelRejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this._notification.set({ title: 'Запит на скасування мийки відхилено', type: 'rejected' });
    });

    toObservable(this.restService.step).pipe(
      pairwise(),
      filter(([prev, curr]) => prev !== 'approved' && curr === 'approved' && prev !== 'cancel-pending'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/rest')) {
        this._notification.set({ title: 'Відпочинок схвалено оператором', type: 'approved' });
      }
    });

    this.restService.rejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/rest')) {
        this._notification.set({ title: 'Запит на відпочинок відхилено оператором', type: 'rejected' });
      }
    });

    this.restService.cancelApprovedConfirmed$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/rest')) {
        this._notification.set({ title: 'Запит на скасування відпочинку схвалено', type: 'approved' });
      }
    });

    this.restService.cancelRejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this._notification.set({ title: 'Запит на скасування відпочинку відхилено', type: 'rejected' });
    });

    toObservable(this.refillService.step).pipe(
      pairwise(),
      filter(([prev, curr]) => prev !== 'approved' && curr === 'approved' && prev !== 'cancel-pending'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/refill')) {
        this._notification.set({ title: 'Заправка схвалена оператором', type: 'approved' });
      }
    });

    this.refillService.rejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/refill')) {
        this._notification.set({ title: 'Запит на заправку відхилено оператором', type: 'rejected' });
      }
    });

    this.refillService.cancelRejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this._notification.set({ title: 'Запит на скасування заправки відхилено', type: 'rejected' });
    });

    // Магніт — схвалено
    this.magnetService.approvedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/magnet')) {
        this._notification.set({ title: 'Магніт схвалено оператором', type: 'approved' });
      }
    });

    // Магніт — відхилено
    this.magnetService.rejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/magnet')) {
        this._notification.set({ title: 'Магніт відхилено оператором', type: 'rejected' });
      }
    });

    // Робот — схвалено
    this.robotService.approvedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/robot')) {
        this._notification.set({ title: 'Запит на робота схвалено', type: 'approved' });
      }
    });

    // Робот — відхилено
    this.robotService.rejectedViaWsEvent$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/robot')) {
        this._notification.set({ title: 'Запит на робота відхилено', type: 'rejected' });
      }
    });

    // Особиста поїздка — старт схвалено
    this.personalTripService.startConfirmed$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/personal-trip')) {
        this._notification.set({ title: 'Особиста поїздка схвалена оператором', type: 'approved' });
      }
    });

    // Особиста поїздка — старт відхилено
    this.personalTripService.startRejected$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/personal-trip')) {
        this._notification.set({ title: 'Запит на особисту поїздку відхилено', type: 'rejected' });
      }
    });

    // Особиста поїздка — завершення схвалено
    this.personalTripService.finishConfirmed$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/personal-trip')) {
        this._notification.set({ title: 'Завершення поїздки підтверджено', type: 'approved' });
      }
    });

    // Особиста поїздка — завершення відхилено
    this.personalTripService.finishRejected$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!this.router.url.startsWith('/cabinet/personal-trip')) {
        this._notification.set({ title: 'Запит на завершення поїздки відхилено', type: 'rejected' });
      }
    });
  }

  dismiss(): void {
    this._notification.set(null);
  }
}
